import { useEffect, useRef, useCallback } from 'react';

/**
 * Hook to prevent content copying, screenshots, and right-click on protected pages
 * Used for simulation pages to protect training content
 * Now includes tracking for admin review
 */
export const useContentProtection = (enabled = true, onViolation = null, sessionId = null) => {
    const violationCountRef = useRef(0);
    
    const reportViolation = useCallback((type, details = {}) => {
        if (!enabled) return;
        
        violationCountRef.current += 1;
        const violation = {
            type,
            timestamp: new Date().toISOString(),
            count: violationCountRef.current,
            sessionId,
            ...details
        };
        
        // Call the callback if provided
        if (onViolation) {
            onViolation(violation);
        }
        
        // Store locally for display
        const stored = JSON.parse(localStorage.getItem('content_violations') || '[]');
        stored.push(violation);
        // Keep only last 100 violations
        if (stored.length > 100) stored.shift();
        localStorage.setItem('content_violations', JSON.stringify(stored));
        
        return violation;
    }, [enabled, onViolation, sessionId]);

    useEffect(() => {
        if (!enabled) return;

        // Prevent right-click context menu
        const handleContextMenu = (e) => {
            e.preventDefault();
            reportViolation('right_click', { target: e.target?.tagName });
            return false;
        };

        // Prevent copy/cut/paste
        const handleCopy = (e) => {
            e.preventDefault();
            reportViolation('copy_attempt', { selection: window.getSelection()?.toString()?.substring(0, 50) });
            return false;
        };
        
        const handleCut = (e) => {
            e.preventDefault();
            reportViolation('cut_attempt');
            return false;
        };

        // Prevent text selection via CSS
        const preventSelection = () => {
            document.body.style.userSelect = 'none';
            document.body.style.webkitUserSelect = 'none';
            document.body.style.msUserSelect = 'none';
            document.body.style.MozUserSelect = 'none';
        };

        // Restore text selection
        const restoreSelection = () => {
            document.body.style.userSelect = '';
            document.body.style.webkitUserSelect = '';
            document.body.style.msUserSelect = '';
            document.body.style.MozUserSelect = '';
        };

        // Prevent keyboard shortcuts for screenshots and copying
        const handleKeyDown = (e) => {
            // Screenshot keys are handled app-wide by the Focus Guard screenshot guard

            // Prevent Ctrl+P (Print)
            if (e.ctrlKey && e.key === 'p') {
                e.preventDefault();
                reportViolation('print_attempt');
                return false;
            }

            // Prevent Ctrl+C (Copy)
            if (e.ctrlKey && e.key === 'c') {
                e.preventDefault();
                reportViolation('copy_keyboard');
                return false;
            }

            // Prevent Ctrl+X (Cut)
            if (e.ctrlKey && e.key === 'x') {
                e.preventDefault();
                reportViolation('cut_keyboard');
                return false;
            }

            // Prevent Ctrl+A (Select All)
            if (e.ctrlKey && e.key === 'a') {
                e.preventDefault();
                reportViolation('select_all_attempt');
                return false;
            }

            // Prevent Ctrl+S (Save)
            if (e.ctrlKey && e.key === 's') {
                e.preventDefault();
                reportViolation('save_attempt');
                return false;
            }

            // Prevent Ctrl+Shift+I (DevTools)
            if (e.ctrlKey && e.shiftKey && e.key === 'I') {
                e.preventDefault();
                reportViolation('devtools_attempt');
                return false;
            }

            // Prevent Ctrl+Shift+J (Console)
            if (e.ctrlKey && e.shiftKey && e.key === 'J') {
                e.preventDefault();
                reportViolation('devtools_console');
                return false;
            }

            // Prevent Ctrl+U (View Source)
            if (e.ctrlKey && e.key === 'u') {
                e.preventDefault();
                reportViolation('view_source_attempt');
                return false;
            }

            // Prevent F12 (DevTools)
            if (e.key === 'F12') {
                e.preventDefault();
                reportViolation('devtools_f12');
                return false;
            }
        };

        // Prevent drag and drop of content
        const handleDragStart = (e) => {
            e.preventDefault();
            reportViolation('drag_attempt');
            return false;
        };
        
        // Detect visibility change (tab switch - possible screenshot via screen recording)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                reportViolation('tab_hidden', { reason: 'Tab switched or minimized' });
            }
        };
        
        // Detect window blur (possible snipping tool activation)
        let blurTimeout = null;
        const handleBlur = () => {
            // Clear any existing timeout
            if (blurTimeout) clearTimeout(blurTimeout);
            
            // Wait a moment before recording - sometimes focus comes right back
            blurTimeout = setTimeout(() => {
                if (!document.hasFocus()) {
                    reportViolation('window_blur', { reason: 'Window lost focus (possible snipping tool)' });
                }
            }, 1000);
        };

        // Add event listeners
        document.addEventListener('contextmenu', handleContextMenu);
        document.addEventListener('copy', handleCopy);
        document.addEventListener('cut', handleCut);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('dragstart', handleDragStart);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);
        preventSelection();

        // Cleanup on unmount
        return () => {
            document.removeEventListener('contextmenu', handleContextMenu);
            document.removeEventListener('copy', handleCopy);
            document.removeEventListener('cut', handleCut);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('dragstart', handleDragStart);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleBlur);
            if (blurTimeout) clearTimeout(blurTimeout);
            restoreSelection();
        };
    }, [enabled, reportViolation]);
    
    return violationCountRef.current;
};

/**
 * Wrapper component for protected content
 * Adds visual indicator and applies protection styles
 * Shows violation count warning to user
 */
export const ProtectedContent = ({ children, showWatermark = false, showViolationCount = false, sessionId = null, onViolation = null }) => {
    const violationCount = useContentProtection(true, onViolation, sessionId);
    
    // Get stored violations count
    const storedViolations = JSON.parse(localStorage.getItem('content_violations') || '[]');
    const sessionViolations = sessionId 
        ? storedViolations.filter(v => v.sessionId === sessionId)
        : storedViolations;

    return (
        <div 
            className="protected-content copy-protected"
            style={{
                position: 'relative',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none'
            }}
        >
            {/* Violation Warning Banner */}
            {showViolationCount && sessionViolations.length > 0 && (
                <div 
                    style={{
                        position: 'fixed',
                        top: '70px',
                        right: '20px',
                        padding: '8px 16px',
                        backgroundColor: 'rgba(239, 68, 68, 0.9)',
                        color: 'white',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: '500',
                        zIndex: 9998,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                    </svg>
                    {sessionViolations.length} security event{sessionViolations.length !== 1 ? 's' : ''} detected
                </div>
            )}
            
            {children}
            
            {showWatermark && (
                <div 
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) rotate(-45deg)',
                        fontSize: '4rem',
                        fontWeight: 'bold',
                        color: 'rgba(0, 0, 0, 0.03)',
                        pointerEvents: 'none',
                        zIndex: 9999,
                        whiteSpace: 'nowrap',
                        userSelect: 'none'
                    }}
                >
                    CONFIDENTIAL
                </div>
            )}
        </div>
    );
};

export default useContentProtection;
